<template>
  <v-card class="mb-4" elevation="2">
    <v-layout row wrap>
      <!-- Hotel Image -->
      <v-flex xs12 sm4 md3 class="pa-2">
        <v-img :src="hotel.HotelImageUrl" height="200" @click="openMainGallery" class="cursor-pointer" style="border-radius: 8px;">
          <template v-slot:placeholder>
            <v-layout fill-height align-center justify-center ma-0>
              <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
            </v-layout>
          </template>
        </v-img>
      </v-flex>

      <!-- Hotel Info -->
      <v-flex xs12 sm8 md7 class="pa-3">
        <div class="" style="height: 100%;">
          <div class="headline font-weight-bold xs12">{{ hotel.HotelName }}</div>
          <div class="mb-1 xs12">
            <v-icon color="yellow darken-2" v-for="n in Math.floor(hotel.Star || 0)" :key="n" small>star</v-icon>
          </div>
          <div class="grey--text text--darken-1 mb-2 xs12">
            <v-icon small class="mr-1">location_on</v-icon>
            {{ hotel.CityName }}, {{ hotel.CountryName }}
          </div>

          <!-- Room Info from PriceResults -->
          <div v-if="hotel.Rooms && hotel.Rooms.length > 0" class="mt-2 d-flex flex-row align-center flex-wrap">
            <div class="subtitle-2 font-weight-bold primary--text mr-3 mb-1">
              <v-icon small color="primary" class="mr-1">hotel</v-icon>
              {{ hotel.Rooms[0].roomName }}
            </div>
            <div class="body-2 grey--text text--darken-2 mr-3 mb-1">
              <v-icon small class="mr-1">restaurant</v-icon>
              {{ hotel.Rooms[0].boardName }}
            </div>
            <div class="body-2 grey--text mb-1">
              <v-icon small class="mr-1">group</v-icon>
              {{ $t('Hotel-Result-Adult-Child', { adt: hotel.Rooms[0].Adt, chd: hotel.Rooms[0].Chd }) }}
            </div>
          </div>

          <v-spacer></v-spacer>

          <div v-if="hotel.CancellationPolicy" class="caption red--text text--darken-2 mt-2">
            <v-icon small color="red darken-2">info</v-icon>
            {{ hotel.CancellationPolicy }}
          </div>
          
          <div v-if="hotel.AcyKomisyon" class="caption green--text text--darken-2 mt-1">
            <v-icon small color="green darken-2">monetization_on</v-icon>
            {{ hotel.AcyKomisyon }}
          </div>
        </div>
      </v-flex>

      <!-- Price and Action -->
      <v-flex xs12 sm12 md2 class="pa-3 text-xs-right d-flex mt-3" style="flex-direction: column; justify-content: space-between;">
        <div>
          <div class="caption grey--text">{{ $t('Hotel-Result-Starting-Price') }}</div>
          <div class="display-1 success--text font-weight-bold">{{ hotel.ServicePrice }} {{ hotel.CurrencyType }}</div>
        </div>
        <div class="mt-1">
          <v-btn color="success" @click="toggleDetails"  :loading="loadingDetails" block>
            {{ showDetails ? $t('Hotel-Result-Hide-Details') : $t('Hotel-Result-Show-Details') }}
          </v-btn>
        </div>
      </v-flex>
    </v-layout>

    <!-- Details Section -->
    <v-expand-transition>
      <div v-show="showDetails" class="grey lighten-4">
        <v-divider></v-divider>
        <div v-if="loadingDetails" class="text-xs-center py-4">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <div class="mt-2">{{ $t('Hotel-Result-Details-Loading') }}</div>
        </div>
        <div v-else>
          <v-tabs v-model="activeTab" color="transparent" slider-color="primary">
            <v-tab key="rooms">{{ $t('Hotel-Result-Tab-Rooms') }}</v-tab>
            <v-tab key="properties">{{ $t('Hotel-Result-Tab-Properties') }}</v-tab>
            <v-tab key="photos">{{ $t('Hotel-Result-Tab-Photos') }}</v-tab>

            <!-- Tab: Odalar -->
            <v-tab-item key="rooms" class="pa-3">
              <v-layout row wrap v-if="displayRooms && displayRooms.length > 0">
                <v-flex xs12 sm6 md4 lg4 v-for="(roomOffer, index) in displayRooms" :key="index" class="pa-2">
                  <hotel-room-card
                    :offer="roomOffer"
                    :hotel="hotel"
                    :sessionHeader="sessionHeader"
                    :searchParams="searchParams"
                    :searchId="searchId"
                    @error="$emit('error', $event)"
                  />
                </v-flex>
              </v-layout>
              <div v-else class="text-xs-center py-3 grey--text">
                {{ $t('Hotel-Result-No-Room-Info') }}
              </div>
            </v-tab-item>

            <!-- Tab: Özellikler -->
            <v-tab-item key="properties" class="pa-3">
              <v-layout row wrap>
                <v-flex xs12 class="mb-3">
                  <v-btn color="primary" @click="openMapsLocation" block>
                    <v-icon small class="mr-2">location_on</v-icon>
                    {{ $t('Hotel-Result-Open-In-Maps') }}
                  </v-btn>
                </v-flex>
                <v-flex xs12 v-if="details.description" class="mb-4">
                  <div class="subtitle-2 font-weight-bold mb-2">{{ $t('Description') }}</div>
                  <div class="body-2 grey--text text--darken-3" v-html="details.description"></div>
                </v-flex>
                <v-flex xs12 v-if="details.hotelServices && details.hotelServices.length > 0" class="mb-3">
                  <div class="subtitle-2 font-weight-bold mb-2">{{ $t('Hotel-Result-Hotel-Services') }}</div>
                  <v-chip v-for="(service, idx) in details.hotelServices" :key="'hs'+idx" small outline color="primary" class="mr-2 mb-2">
                    {{ service.FilterName || service.Name || $t('Hotel-Result-Service-Fallback') }}
                  </v-chip>
                </v-flex>
                <v-flex xs12 v-if="details.hotelProperties && details.hotelProperties.length > 0" class="mb-3">
                  <div class="subtitle-2 font-weight-bold mb-2">{{ $t('Hotel-Result-Hotel-Properties') }}</div>
                  <v-chip v-for="(prop, idx) in details.hotelProperties" :key="'hp'+idx" small outline color="secondary" class="mr-2 mb-2">
                    {{ prop.FilterName || $t('Hotel-Result-Property-Fallback') }}
                  </v-chip>
                </v-flex>
                <v-flex xs12 v-if="details.roomProperties && details.roomProperties.length > 0" class="mb-3">
                  <div class="subtitle-2 font-weight-bold mb-2">{{ $t('Hotel-Result-Room-Properties') }}</div>
                  <v-chip v-for="(rprop, idx) in details.roomProperties" :key="'rp'+idx" small outline color="info" class="mr-2 mb-2">
                    {{ rprop.Name || 'Özellik' }}
                  </v-chip>
                </v-flex>
                <v-flex xs12 v-if="(!details.description) && (!details.hotelServices || details.hotelServices.length === 0) && (!details.hotelProperties || details.hotelProperties.length === 0) && (!details.roomProperties || details.roomProperties.length === 0)">
                  <div class="text-xs-center py-3 grey--text">{{ $t('Hotel-Result-No-Property-Info') }}</div>
                </v-flex>
              </v-layout>
            </v-tab-item>

            <!-- Tab: Fotoğraflar -->
            <v-tab-item key="photos" class="pa-3">
              <div v-if="allImages.length > 0">
                <v-layout row wrap>
                  <v-flex 
                    :xs6="$vuetify.breakpoint.xsOnly" 
                    :xs4="$vuetify.breakpoint.smOnly" 
                    :xs3="$vuetify.breakpoint.mdAndUp" 
                    v-for="(img, idx) in paginatedImages" 
                    :key="idx" 
                    class="pa-1">
                    <v-img 
                      :src="img.Url" 
                      aspect-ratio="1" 
                      class="grey lighten-2 cursor-pointer img-thumbnail" 
                      @click="openImage((photoPage - 1) * itemsPerPage + idx)"
                    >
                      <template v-slot:placeholder>
                        <v-layout fill-height align-center justify-center ma-0>
                          <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                        </v-layout>
                      </template>
                    </v-img>
                  </v-flex>
                </v-layout>
                <div class="text-xs-center mt-3" v-if="totalPhotoPages > 1">
                  <v-pagination v-model="photoPage" :length="totalPhotoPages" total-visible="7"></v-pagination>
                </div>
              </div>
              <div v-else class="text-xs-center py-3 grey--text">
                {{ $t('Hotel-Result-No-Photos') }}
              </div>
            </v-tab-item>
          </v-tabs>
        </div>
      </div>
    </v-expand-transition>

    <!-- Image Dialog -->
    <v-dialog v-model="imageDialog" max-width="900px">
      <v-card class="bg-black" color="black">
        <v-toolbar dark color="transparent" flat>
          <v-spacer></v-spacer>
          <v-btn icon @click="imageDialog = false"><v-icon>close</v-icon></v-btn>
        </v-toolbar>
        <v-carousel v-if="imageDialog" v-model="currentImageIndex" height="600" hide-delimiters>
          <v-carousel-item v-for="(img, idx) in allImages" :key="idx">
            <v-layout fill-height align-center justify-center>
              <img :src="img.Url" style="max-height: 100%; max-width: 100%; object-fit: contain;" />
            </v-layout>
          </v-carousel-item>
        </v-carousel>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import HotelRoomCard from './HotelRoomCard.vue';

export default {
  name: 'HotelResultCard',
  components: {
    HotelRoomCard
  },
  props: {
    hotel: {
      type: Object,
      required: true
    },
    details: {
      type: Object,
      default: () => ({
        rooms: [],
        hotelProperties: [],
        hotelServices: [],
        roomProperties: [],
        hotelImages: [],
        roomImages: []
      })
    },
    loadingDetails: {
      type: Boolean,
      default: false
    },
    sessionHeader: {
      type: Object,
      default: null
    },
    searchParams: {
      type: Object,
      default: () => ({})
    },
    searchId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      showDetails: false,
      activeTab: 0,
      imageDialog: false,
      currentImageIndex: 0,
      photoPage: 1
    }
  },
  watch: {
    totalPhotoPages(newVal) {
      if (this.photoPage > newVal) {
        this.photoPage = Math.max(1, newVal);
      }
    }
  },
  computed: {
    itemsPerPage() {
      if (!this.$vuetify) return 4;
      if (this.$vuetify.breakpoint.xsOnly) return 2;
      if (this.$vuetify.breakpoint.smOnly) return 3;
      return 4;
    },
    totalPhotoPages() {
      return Math.ceil(this.allImages.length / this.itemsPerPage) || 1;
    },
    paginatedImages() {
      const start = (this.photoPage - 1) * this.itemsPerPage;
      return this.allImages.slice(start, start + this.itemsPerPage);
    },
    displayRooms() {
      if (this.details && this.details.rooms && this.details.rooms.length > 0) {
        return this.details.rooms;
      }
      // Fallback to the hotel object itself as a room offer if it has Rooms
      if (this.hotel && this.hotel.Rooms && this.hotel.Rooms.length > 0) {
        return [this.hotel];
      }
      return [];
    },
    allImages() {
      const hImages = this.details.hotelImages || [];
      const rImages = this.details.roomImages || [];
      // Combine and filter out duplicates based on Url
      const combined = [...hImages, ...rImages];
      const uniqueUrls = new Set();
      return combined.filter(img => {
        if (!uniqueUrls.has(img.Url)) {
          uniqueUrls.add(img.Url);
          return true;
        }
        return false;
      });
    }
  },
  methods: {
    toggleDetails() {
      this.showDetails = !this.showDetails;
      if (this.showDetails && (!this.details.rooms || this.details.rooms.length === 0)) {
        this.$emit('fetch-details', this.hotel.HotelId);
      }
    },
    openImage(index) {
      this.currentImageIndex = index;
      this.imageDialog = true;
    },
    openMainGallery() {
      // If we don't have images loaded yet, request details first
      if (this.allImages.length === 0 && !this.loadingDetails) {
        this.showDetails = true; // Auto-open details so user sees loading state
        this.activeTab = 2; // Default to photos tab
        this.$emit('fetch-details', this.hotel.HotelId);

        // Wait for loading to finish, then open dialog
        const unwatch = this.$watch('loadingDetails', (newVal) => {
          if (!newVal) {
            unwatch();
            if (this.allImages.length > 0) {
              this.currentImageIndex = 0;
              this.imageDialog = true;
            }
          }
        });
      } else if (this.allImages.length > 0) {
        this.currentImageIndex = 0;
        this.imageDialog = true;
      }
    },
    openMapsLocation() {
      const lat = (this.hotel.Latitude || 0) / 1000000;
      const lng = (this.hotel.Longitude || 0) / 1000000;
      const mapsUrl = `https://www.google.com/maps/?q=${lat},${lng}`;
      window.open(mapsUrl, '_blank');
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.img-thumbnail {
  border-radius: 4px;
  transition: transform 0.2s;
}
.img-thumbnail:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
}
.bg-black {
  background-color: #000000 !important;
}
</style>
